/* ============================================================================
   The interactive FW-1.

   A working replica of the device face, driven by the same two gestures the
   hardware has: TWIST to change a value, PRESS to commit. Nobody can pick this
   product up before buying it — it does not ship yet — so the next best thing
   is letting them drive it.

   Screens follow img/face/dark/*.png, which are photographs of the real
   firmware, not invention: queue -> length -> running -> complete -> break.

   Everything is a fraction of --d (the device diameter) so one clamp in the CSS
   scales the whole face. Type is deliberately LARGER relative to the disc than
   the real firmware: at 335px across, honest proportions put the labels near
   9px, which is the same unreadable-at-phone-width trap the infographics fell
   into. This is an interface, not a photograph of one.

   Guarded on #fw: an unguarded getElementById here would throw on every
   template without the hero and abort whatever runs after it.
   ========================================================================= */
(function () {
  var root = document.getElementById('fw');
  if (!root) return;

  var faceEl   = document.getElementById('fwFace');
  var pressEl  = document.getElementById('fwPress');
  var arcEl    = document.getElementById('fwArc');
  var knobEl   = document.getElementById('fwKnob');
  var statusEl = document.getElementById('fwStatus');
  var hintEl   = document.getElementById('fwHint');
  if (!faceEl || !pressEl || !arcEl) return;

  /* ---- data ---------------------------------------------------------- */
  var TASKS = [];
  try { TASKS = JSON.parse(root.getAttribute('data-tasks') || '[]'); } catch (e) {}
  if (!TASKS.length) {
    TASKS = [{ t: 'Update client website copy', c: 'Peak Studio', done: 1, of: 4 }];
  }

  /* The complete screen is the conversion moment: someone has just driven the
     product through a whole session. Both destinations come from Liquid so the
     copy can never drift from the discount code that actually exists. */
  var BUY_URL     = root.getAttribute('data-buy')   || '/';
  var BUY_LABEL   = root.getAttribute('data-buy-label')   || 'Buy now';
  var OFFER_URL   = root.getAttribute('data-offer') || '';
  var OFFER_LABEL = root.getAttribute('data-offer-label') || '';

  var PRESETS = [5, 10, 15, 30, 60];   // as on 03-duration-25.png; 25 is the default value, not a chip
  var MIN_M = 5, MAX_M = 60;

  /* A real 25-minute countdown would show a bar that never visibly moves, so
     the demo compresses any session into about eleven seconds and any break
     into five. The clock still reads the length you actually chose — it just
     runs fast, which the caption under the device says out loud. */
  var RUN_MS = 11000, BREAK_MS = 5000;

  var state = 'length';     // queue | length | running | complete | pause
                            // boots on the timer screen: it is the screen that shows
                            // the gesture, and the queue is one press away from it
  var sel = 0;              // index into TASKS
  var mins = 25;            // chosen length
  var startedAt = 0, totalMs = 0, raf = null;

  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---- geometry -------------------------------------------------------
     The arc is an explicit path, recomputed per update, rather than a dashed
     circle. Dash offsets require the browser and the author to agree on the
     path length and they do not: Chrome measures this circle as 282.287 user
     units against a computed 2*pi*r of 282.74, and an offset longer than the
     real path wraps the pattern back into view - an "empty" arc drew a full
     ring. This has no such failure mode and is trivially inspectable. */
  var R = 45;
  function pt(deg) {
    var a = deg * Math.PI / 180;
    return { x: +(50 + R * Math.cos(a)).toFixed(3), y: +(50 + R * Math.sin(a)).toFixed(3) };
  }
  function setArc(frac) {
    frac = Math.max(0, Math.min(1, frac));
    if (frac < 0.001) {
      arcEl.setAttribute('d', '');
      if (knobEl) knobEl.setAttribute('opacity', '0');
      return;
    }
    var f = Math.min(frac, 0.9999);          // a single A cannot sweep a full 360
    var p0 = pt(-90), p1 = pt(-90 + f * 360);
    arcEl.setAttribute('d', 'M' + p0.x + ' ' + p0.y +
      ' A' + R + ' ' + R + ' 0 ' + (f > 0.5 ? 1 : 0) + ' 1 ' + p1.x + ' ' + p1.y);
    if (!knobEl) return;
    knobEl.setAttribute('opacity', '1');
    knobEl.setAttribute('cx', p1.x);
    knobEl.setAttribute('cy', p1.y);
  }

  function mmss(ms) {
    var s = Math.max(0, Math.ceil(ms / 1000));
    return Math.floor(s / 60) + ':' + String(s % 60).padStart(2, '0');
  }
  function esc(s) {
    return String(s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }

  /* ---- screens -------------------------------------------------------- */
  function screenQueue() {
    /* A three-row window centred on the selection, like 02-task-list.png. Five
       full rows do not fit inside a circle without shrinking the type past
       legibility, and the disc is the constraint the real device has too. */
    var n = TASKS.length, win = Math.min(3, n);
    var start = Math.max(0, Math.min(sel - 1, n - win));
    var rows = '';
    for (var i = start; i < start + win; i++) {
      var t = TASKS[i];
      rows += '<li class="fw-row' + (i === sel ? ' on' : '') + '" data-task="' + i + '">' +
              '<span class="fw-bul"></span>' +
              '<span class="fw-rt"><b>' + esc(t.t) + '</b>' +
              '<i>' + esc(t.c) + ' &middot; ' + t.done + '/' + t.of + '</i></span></li>';
    }
    return '<span class="fw-k">Tasks &middot; ' + TASKS.length + '</span>' +
           '<ul class="fw-list">' + rows + '</ul>' +
           '<span class="fw-foot">Press to select</span>';
  }

  function screenLength() {
    var chips = PRESETS.map(function (p) {
      return '<button type="button" class="fw-chip' + (p === mins ? ' on' : '') +
             '" data-min="' + p + '">' + p + 'm</button>';
    }).join('');
    return '<span class="fw-k">Focus length</span>' +
           '<span class="fw-task">' + esc(TASKS[sel].t) + '</span>' +
           '<span class="fw-tw">&lt; twist &nbsp;tap to start &gt;</span>' +
           '<span class="fw-big">' + mins + '</span>' +
           '<span class="fw-unit">minutes</span>' +
           '<div class="fw-chips">' + chips + '</div>';
  }

  function screenRunning(left) {
    return '<span class="fw-k">Focus</span>' +
           '<span class="fw-clock">' + mmss(left) + '</span>' +
           '<span class="fw-task">' + esc(TASKS[sel].t) + '</span>' +
           '<span class="fw-foot">Press when done</span>';
  }

  function screenBreak(left) {
    return '<span class="fw-k">Break</span>' +
           '<span class="fw-clock">' + mmss(left) + '</span>' +
           '<span class="fw-foot">Back in a moment</span>';
  }

  function screenComplete() {
    /* Mirrors the real firmware's complete screen (06-complete): an accent
       "SESSION COMPLETE" eyebrow over a "Locked in." statement, then two pills.
       The device offers "Take 5m break" / "Done" there; this is the conversion
       moment, so the two pills are the offer and the buy instead.

       ⚠️ No task name here, deliberately: eyebrow + statement + two pills + foot
       is already the most a disc will hold. Adding a line clips the eyebrow into
       the narrow top of the circle - the same failure the .fw-face padding rules
       above were written to avoid. Measure before adding anything. */
    var ctas = '';
    if (OFFER_LABEL) {
      ctas += '<button type="button" class="fw-cta" data-offer>' + esc(OFFER_LABEL) + '</button>';
    }
    ctas += '<a class="fw-cta fw-cta--go" href="' + esc(BUY_URL) + '">' + esc(BUY_LABEL) + '</a>';
    /* ⚠️ NO .fw-foot here. The firmware's complete screen has no footer line
       either, and measured 2026-09-10 there is no room for one: with the foot in
       place the CTAs ended at 203 against a foot starting at 200 (a 3px overlap),
       and the eyebrow was forced up to a chord only ~142px wide while "Session
       complete" measures 157px - clipped at both ends. The "press to continue"
       affordance moved to the hint line UNDER the disc, where width is free. */
    return '<span class="fw-k">Session complete</span>' +
           '<span class="fw-done">Locked in.</span>' +
           '<div class="fw-ctas">' + ctas + '</div>';
  }

  /* ---- render --------------------------------------------------------- */
  var LABEL = {
    queue:    'Task queue. Arrow keys choose a task, press to select.',
    length:   'Set the session length. Arrow keys twist, press to start.',
    running:  'Session running. Press when done.',
    pause:    'Break running. Press to skip.',
    complete: 'Session complete. Press to keep going, or take one of the two offers.'
  };

  function render(left) {
    if (state === 'queue') {
      faceEl.innerHTML = screenQueue();
      setArc(0);
    } else if (state === 'length') {
      faceEl.innerHTML = screenLength();
      setArc(mins / MAX_M);
    } else if (state === 'running') {
      faceEl.innerHTML = screenRunning(left);
      setArc(left / (mins * 60000));
    } else if (state === 'pause') {
      faceEl.innerHTML = screenBreak(left);
      setArc(left / (5 * 60000));
    } else {
      faceEl.innerHTML = screenComplete();
      setArc(1);
    }
    root.setAttribute('data-state', state);
    pressEl.setAttribute('aria-label', LABEL[state]);
    if (hintEl) hintEl.textContent =
      state === 'length'   ? 'Twist the ring — drag, scroll or arrow keys'
    : state === 'complete' ? 'Press the face to run it again'
    :                        'Demo — the clock runs at speed';
  }

  var lastSaid = '';
  function say(msg) {
    if (!statusEl || msg === lastSaid) return;
    lastSaid = msg; statusEl.textContent = msg;
  }

  /* ---- the running clock ---------------------------------------------- */
  function tick() {
    var elapsed = performance.now() - startedAt;
    var frac = Math.min(1, elapsed / totalMs);
    var span = (state === 'running' ? mins * 60000 : 5 * 60000);
    var left = span * (1 - frac);
    if (state === 'running') faceEl.innerHTML = screenRunning(left);
    else faceEl.innerHTML = screenBreak(left);
    setArc(1 - frac);
    if (frac >= 1) { stop(); finish(); return; }
    raf = requestAnimationFrame(tick);
  }
  function run(ms) {
    stop(); startedAt = performance.now(); totalMs = ms;
    if (reduce) { render(0); return; }      // no animated drain; still advances on press
    raf = requestAnimationFrame(tick);
  }
  function stop() { if (raf) { cancelAnimationFrame(raf); raf = null; } }

  function finish() {
    if (state === 'running') {
      TASKS[sel].done = Math.min(TASKS[sel].of, TASKS[sel].done + 1);
      state = 'complete'; render(); say('Session complete.');
    } else {
      state = 'queue'; sel = (sel + 1) % TASKS.length; render(); say('Break over. Next task.');
    }
  }

  /* ---- the two gestures ------------------------------------------------ */
  function press() {
    releaseScroll();
    if (state === 'queue') {
      state = 'length'; render();
      say('Selected ' + TASKS[sel].t + '. Twist to set the length.');
    } else if (state === 'length') {
      state = 'running'; render(mins * 60000); run(RUN_MS);
      say('Running ' + mins + ' minutes on ' + TASKS[sel].t + '.');
    } else if (state === 'running') {
      stop(); finish();
    } else if (state === 'pause') {
      stop(); state = 'queue'; sel = (sel + 1) % TASKS.length; render(); say('Next task.');
    } else {
      state = 'queue'; sel = (sel + 1) % TASKS.length; render(); say('Back to the queue.');
    }
  }

  /* "Forward" means different things in the two states that take a value, and
     the arrow keys have to respect both conventions: ArrowDown moves DOWN a
     list, but ArrowUp INCREASES a number. So the dial exposes one forward/back
     step, and the key handler decides which key means forward in this state. */
  function moveSel(d) {
    releaseScroll();
    sel = (sel + d + TASKS.length) % TASKS.length;
    render(); say(TASKS[sel].t);
  }
  function setMins(d) {
    releaseScroll();
    mins = Math.max(MIN_M, Math.min(MAX_M, mins + d * 5));
    render(); say(mins + ' minutes');
  }
  /* clockwise on the ring, or scrolling down, is always "forward" */
  function spin(d) {
    if (state === 'queue') moveSel(d);
    else if (state === 'length') setMins(d);
  }

  /* ---- input -----------------------------------------------------------
     ONE delegated click listener, on the root.

     ⚠️ It cannot live on the screen or the button. `setPointerCapture` below
     retargets the pointerup to the root, and a `click` fires on the nearest
     common ancestor of the down and up targets - so with capture in play the
     click lands on `.fw` and never reaches a listener on a descendant. That is
     what broke the preset chips AND tap-to-start: every real tap was silently
     swallowed. `element.click()` dispatches straight on the node and skips
     pointer capture entirely, which is why every test passed. Test with real
     pointer events. */
  root.addEventListener('click', function (e) {
    if (dragged) { dragged = false; return; }      // a drag is not a tap

    if (e.target.closest('[data-offer]')) { openOffer(); return; }

    var chip = e.target.closest('.fw-chip');
    if (chip) {
      if (chip.dataset.min) { releaseScroll(); mins = parseInt(chip.dataset.min, 10); render(); say(mins + ' minutes'); return; }
      return;
    }

    var row = e.target.closest('.fw-row');
    if (row) { sel = parseInt(row.dataset.task, 10); press(); return; }

    if (e.target.closest('.fw-press')) press();
  });

  pressEl.addEventListener('keydown', function (e) {
    var k = e.key;
    if (k !== 'ArrowUp' && k !== 'ArrowDown' && k !== 'ArrowLeft' && k !== 'ArrowRight') return;
    e.preventDefault();
    if (state === 'queue') {
      // down/right walks down the list, the way every list works
      moveSel(k === 'ArrowDown' || k === 'ArrowRight' ? 1 : -1);
    } else if (state === 'length') {
      // up/right raises the number, the way every stepper works
      setMins(k === 'ArrowUp' || k === 'ArrowRight' ? 1 : -1);
    }
  });

  /* Drag the bezel like the real ring: the angle from the centre, not the
     distance dragged, so it behaves like a dial rather than a slider.

     ⚠️ Pointer capture is taken only AFTER movement passes DRAG_MIN. Capturing
     on pointerdown retargets the click to the root and kills every tap on the
     screen - see the click listener above. Capturing late also gives correct
     tap-versus-drag discrimination for free: if we captured, it was a drag. */
  var DRAG_MIN = 6;
  var down = false, dragged = false, sx0 = 0, sy0 = 0, lastAng = 0, acc = 0;
  function angleOf(e) {
    var b = root.getBoundingClientRect();
    return Math.atan2(e.clientY - (b.top + b.height / 2), e.clientX - (b.left + b.width / 2)) * 180 / Math.PI;
  }
  root.addEventListener('pointerdown', function (e) {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    if (state !== 'length' && state !== 'queue') return;
    down = true; dragged = false; acc = 0;
    sx0 = e.clientX; sy0 = e.clientY; lastAng = angleOf(e);
  });
  root.addEventListener('pointermove', function (e) {
    if (!down) return;
    if (!dragged) {
      var dx = e.clientX - sx0, dy = e.clientY - sy0;
      if (Math.sqrt(dx * dx + dy * dy) < DRAG_MIN) return;
      dragged = true;
      // capture so a release outside the device still completes the gesture
      try { root.setPointerCapture(e.pointerId); } catch (err) {}
      lastAng = angleOf(e);                 // re-baseline so the first detent is not jumpy
      return;
    }
    var a = angleOf(e), d = a - lastAng;
    if (d > 180) d -= 360; else if (d < -180) d += 360;   // wrap at the seam
    lastAng = a; acc += d;
    while (Math.abs(acc) >= 18) {                          // one detent per 18 degrees
      spin(acc > 0 ? 1 : -1);
      acc -= (acc > 0 ? 18 : -18);
    }
  });
  function endDrag() { down = false; }
  root.addEventListener('pointerup', endDrag);
  root.addEventListener('pointercancel', function () { down = false; dragged = false; });
  root.addEventListener('dragstart', function (e) { e.preventDefault(); });

  /* Scroll to twist, but only a gesture whose intent is clearly the dial:
     claimed only in the two states that take a value, and only past a
     threshold, so the page keeps scrolling normally the rest of the time. */
  var wacc = 0, wlock = false, wtimer = null;
  root.addEventListener('wheel', function (e) {
    if (state !== 'length' && state !== 'queue') return;
    if (Math.abs(e.deltaY) < Math.abs(e.deltaX)) return;
    if (wlock) { e.preventDefault(); return; }
    wacc += e.deltaY;
    clearTimeout(wtimer); wtimer = setTimeout(function () { wacc = 0; }, 160);
    if (Math.abs(wacc) < 40) return;
    e.preventDefault();
    spin(wacc > 0 ? 1 : -1);
    wacc = 0; wlock = true;
    setTimeout(function () { wlock = false; }, 140);
  }, { passive: false });

  /* ---- the offer dialog -------------------------------------------------
     Opened from the session-complete screen. The form inside is Shopify's own
     {% form 'customer' %}, so it needs no endpoint and works with no JS: it
     posts, Shopify records the subscriber, and Liquid renders the revealed-code
     state on the way back. This only handles opening, closing and focus. */
  var modal = document.getElementById('fwOffer');
  var lastFocus = null;

  function focusables() {
    return modal ? modal.querySelectorAll('a[href],button:not([disabled]),input,[tabindex]:not([tabindex="-1"])') : [];
  }
  function openOffer() {
    if (!modal) return;
    lastFocus = document.activeElement;
    modal.hidden = false;
    document.documentElement.style.overflow = 'hidden';
    var f = focusables();
    if (f.length) f[0].focus();
  }
  function closeOffer() {
    if (!modal || modal.hidden) return;
    modal.hidden = true;
    document.documentElement.style.overflow = '';
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }
  /* ⚠️ Shopify's spam protection (hCaptcha) intercepts this form's submit and can
     escalate to a visible image challenge, rendered in an iframe appended to the
     BODY - outside this dialog. A hard focus trap would then lock a keyboard user
     out of the very challenge they have to solve, and Escape would shut the dialog
     from under it. So both step aside while a challenge is on screen. */
  function captchaOpen() {
    var f = document.querySelectorAll('iframe[src*="hcaptcha"]');
    for (var i = 0; i < f.length; i++) {
      var r = f[i].getBoundingClientRect();
      if (r.width > 60 && r.height > 60) return true;
    }
    return false;
  }

  /* ---- unique codes, when offer-service is configured -------------------
     With no endpoint the dialog is Shopify's own {% form 'customer' %}: it
     posts, Shopify records the subscriber, and Liquid renders the shared code.
     That path stays the fallback and still works with no JavaScript.

     With an endpoint set, this intercepts the submit, asks the service for a
     code derived from that address, and swaps the card in place - no reload, and
     each subscriber gets their own single-use code. If the request fails for any
     reason it does NOT swallow the submit: it lets the native Shopify post go
     ahead, so the visitor still ends up subscribed with the shared code. */
  var ENDPOINT = root.getAttribute('data-endpoint') || '';
  var VARIANT  = root.getAttribute('data-variant') || '';

  /* ---- where "Apply it" goes -------------------------------------------
     Straight to checkout with the code applied. WHICH url does that depends on
     the cart, and both forms were measured against the live store on 2026-09-10:

     - Cart EMPTY -> `/cart/<variant>:1?discount=<code>` creates the cart AND
       redirects to /checkouts/cn/... with the code on. The other form would land
       on an EMPTY checkout (Shopify's EmptyState) - a dead end.
     - Cart NOT empty -> `/discount/<code>?redirect=/checkout` keeps what they
       already have. ⚠️ The permalink REPLACES the cart: in testing a 2-unit cart
       at $258.00 silently became 1 unit at $126.65. Never send an existing cart
       to the permalink.

     Anything unknown (no variant, or /cart.js fails) degrades to the product
     page. That loses the direct-to-checkout jump, which is a worse outcome than
     nothing - but an empty checkout or a halved cart is worse still. */
  function checkoutHref(code) {
    var safe     = '/discount/' + encodeURIComponent(code) +
                   '?redirect=' + encodeURIComponent(BUY_URL);
    var keepCart = '/discount/' + encodeURIComponent(code) + '?redirect=/checkout';
    var fresh    = VARIANT ? '/cart/' + encodeURIComponent(VARIANT) + ':1?discount=' +
                             encodeURIComponent(code) : null;
    if (!fresh) return Promise.resolve(safe);
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (c) { return (c && c.item_count > 0) ? keepCart : fresh; })
      .catch(function () { return safe; });
  }

  function revealCode(code, apply) {
    var card = modal.querySelector('.fwm-card');
    if (!card) return;
    card.innerHTML =
      '<button type="button" class="fwm-x" data-close aria-label="Close">' +
      '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
      'stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>' +
      '</button>' +
      '<h2 class="fwm-h">Here&rsquo;s your 15% off.</h2>' +
      '<p class="fwm-code"><code>' + esc(code) + '</code></p>' +
      '<a class="btn btn-primary btn-block" href="' + apply + '">Apply it</a>';
    var a = card.querySelector('.btn');
    if (a) a.focus();
    say('Your code is ' + code);
  }

  /* ---- the offer dialog's one and only capture path ---------------------
     ⚠️ THERE IS NO LONGER A FORM HERE, AND NO FALLBACK. Removed 2026-09-10 with
     the shared code, at Alan's request. Do not reintroduce either:

     - The Shopify form was never reachable without JS anyway: the pill that opens
       this dialog is rendered by THIS FILE, so no JS means no button.
     - It actively destroyed codes. Shopify's hCaptcha attaches its own submit
       listener and re-submits the form PROGRAMMATICALLY - which does not fire the
       submit event, so no listener of ours could stop it. The page navigated and
       Liquid replaced a freshly minted unique code with the shared one.

     With a plain <div> and a type="button" button there is no submit event at all,
     so there is nothing for hCaptcha to attach to. A failed mint now says so in
     the dialog rather than silently posting somewhere else. */
  if (modal && ENDPOINT) {
    var goBtn = document.getElementById('fwmGo');
    var errEl = document.getElementById('fwmErr');
    if (goBtn) {
      var busy = false;

      function showErr(msg) {
        if (!errEl) return;
        errEl.textContent = msg;
        errEl.hidden = false;
      }

      function getCode() {
        if (busy) return;
        var input = document.getElementById('fwmEmail');
        if (!input) return;
        /* Not a submit button, so the browser will not validate on its own. */
        if (!input.value || (input.checkValidity && !input.checkValidity())) {
          if (input.reportValidity) input.reportValidity();
          return;
        }
        if (errEl) errEl.hidden = true;
        busy = true;
        goBtn.disabled = true;
        goBtn.textContent = 'One moment';
        /* A hung request would otherwise strand the dialog's only button on
           "One moment" forever, at the conversion moment. */
        var ctl = ('AbortController' in window) ? new AbortController() : null;
        var timer = setTimeout(function () { if (ctl) ctl.abort(); }, 12000);
        fetch(ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: input.value }),
          signal: ctl ? ctl.signal : undefined
        }).then(function (r) { return r.json().then(function (j) { return { ok: r.ok, j: j }; }); })
          .then(function (res) {
            clearTimeout(timer);
            if (res.ok && res.j && res.j.code) {
              var c = res.j.code;
              return checkoutHref(c).then(function (href) { revealCode(c, href); });
            }
            throw new Error((res.j && res.j.error) || 'no code');
          })
          .catch(function (e) {
            clearTimeout(timer);
            busy = false;
            goBtn.disabled = false;
            goBtn.textContent = 'Get the code';
            showErr('That did not go through. Try again in a moment.');
          });
      }

      goBtn.addEventListener('click', getCode);
      /* Enter in the field should still work, even with no form around it. */
      var emailEl = document.getElementById('fwmEmail');
      if (emailEl) emailEl.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') { e.preventDefault(); getCode(); }
      });
    }
  }

  /* ---- the timed offer popup -------------------------------------------
     Opens the SAME dialog the wheel's pill opens. ⚠️ Not a second capture path:
     one mechanism mints codes, and that rule is what keeps the margin traceable.

     ⚠️ Fires ONCE PER BROWSER and never again. The flag is written when it
     OPENS, not when it converts, so dismissing it is not punished with a repeat
     on the next page view. Clearing it is a localStorage delete.

     Every guard below exists for a reason:
       - localStorage throws in private mode / with cookies blocked, so it is
         wrapped. An uncaught throw here would abort the rest of this IIFE - the
         exact failure mode theme.js already shipped once and took the mobile
         menu down with it.
       - already open, or a code already on screen -> stay out.
       - tab hidden -> wait for it to come back, rather than burning the one
         showing on a background tab nobody sees.
       - mid-drag on the wheel -> defer. Cutting across the demo gesture is the
         one moment a modal is genuinely destructive rather than merely rude. */
  var POPUP_S = parseFloat(root.getAttribute('data-popup') || '0');
  if (modal && ENDPOINT && POPUP_S > 0) {
    var SEEN_KEY = 'fw-offer-popup';
    var alreadySeen = false;
    try { alreadySeen = localStorage.getItem(SEEN_KEY) === '1'; } catch (e) {}
    if (!alreadySeen) {
      var firePopup = function () {
        if (!modal.hidden) return;                        // open already
        if (modal.querySelector('.fwm-code')) return;     // they already have a code
        if (document.hidden) {
          var onBack = function () {
            document.removeEventListener('visibilitychange', onBack);
            setTimeout(firePopup, 500);
          };
          document.addEventListener('visibilitychange', onBack);
          return;
        }
        if (down) { setTimeout(firePopup, 1200); return; } // mid-gesture, wait
        try { localStorage.setItem(SEEN_KEY, '1'); } catch (e) {}
        openOffer();
        say('Offer: 15% off your first wheel.');
      };
      setTimeout(firePopup, POPUP_S * 1000);
    }
  }

  if (modal) {
    modal.addEventListener('click', function (e) {
      if (e.target.closest('[data-close]')) { e.preventDefault(); closeOffer(); }
    });
    document.addEventListener('keydown', function (e) {
      if (modal.hidden) return;
      if (captchaOpen()) return;                 // the challenge owns the keyboard
      if (e.key === 'Escape') { closeOffer(); return; }
      if (e.key !== 'Tab') return;
      // keep Tab inside the dialog
      var f = focusables();
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
  }

  /* ---- scroll winds the dial --------------------------------------------
     Page scroll across the hero maps onto the session length: at rest the face
     reads 25, the value the render and the firmware screenshot both show, and
     scrolling down winds it up to 60. It is the same gesture as the physical
     ring, driven by the only input every visitor already uses.

     It yields permanently the moment someone touches the dial directly - drag,
     arrow, chip or press - because fighting a deliberate choice is worse than
     not having the effect at all. Only ever active on the timer screen. */
  var SCROLL_MIN = 25, SCROLL_MAX = MAX_M;
  var scrollLinked = true;
  var heroEl = root.closest('.herowrap') || root.parentElement;

  function releaseScroll() { scrollLinked = false; }

  function scrollWind() {
    if (!scrollLinked || state !== 'length' || !heroEl) return;
    var b = heroEl.getBoundingClientRect();
    if (!b.height) return;
    var p = Math.max(0, Math.min(1, -b.top / b.height));
    var steps = (SCROLL_MAX - SCROLL_MIN) / 5;
    var m = SCROLL_MIN + Math.round(p * steps) * 5;
    if (m !== mins) { mins = m; render(); }
  }
  window.addEventListener('scroll', scrollWind, { passive: true });
  window.addEventListener('resize', scrollWind);

  /* Pause the clock while off screen so a backgrounded hero is not animating. */
  if ('IntersectionObserver' in window) {
    new IntersectionObserver(function (es) {
      es.forEach(function (en) {
        if (!en.isIntersecting && raf) stop();
        else if (en.isIntersecting && !raf && (state === 'running' || state === 'pause')) run(totalMs);
      });
    }, { threshold: 0.1 }).observe(root);
  }

  render();
})();
